import { useState } from 'react';
import { format, isPast } from 'date-fns';
import { Plus, Calendar, MapPin, Edit2, CheckCircle, Trash2, Webhook } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';
import { CreateActivityDialog } from './CreateActivityDialog';
import { captureError } from '@/lib/sentry';

interface Activity {
  id: string;
  type: 'in_person' | 'phone_call' | 'video_call' | 'email' | 'sms' | 'note' | 'calendar_event' | 'api_update';
  title: string;
  description?: string;
  start_date: string;
  end_date?: string;
  location?: string;
  completed: boolean;
  scheduled: boolean;
}

/**
 * The manual-activity editor (CreateActivityDialog) only knows the five
 * user-created activity types. System-generated entries — notes, calendar
 * events, and API-provenance rows ('api_update') — appear in the feed for
 * context but are not editable through that dialog, so the Edit affordance is
 * hidden for them and `editingActivity` is narrowed to the editable subset.
 */
const EDITABLE_ACTIVITY_TYPES = ['in_person', 'phone_call', 'video_call', 'email', 'sms'] as const;
type EditableActivity = Activity & { type: (typeof EDITABLE_ACTIVITY_TYPES)[number] };
function isEditableActivity(activity: Activity): activity is EditableActivity {
  return (EDITABLE_ACTIVITY_TYPES as readonly string[]).includes(activity.type);
}

export function ContactActivitiesTab({ contactId }: { contactId: string }) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<EditableActivity | null>(null);
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const queryKey = queryKeys.activities.contact(contactId, currentWorkspace?.id);

  const { data: activities = [], isLoading: loading } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!currentWorkspace) return [];

      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .eq('contact_id', contactId)
        .eq('workspace_id', currentWorkspace.id)
        .order('start_date', { ascending: false });

      if (error) throw error;
      return (data || []) as Activity[];
    },
    enabled: !!currentWorkspace?.id,
    staleTime: 30_000,
  });

  const invalidateActivities = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });
  };

  const handleComplete = async (activityId: string) => {
    try {
      const { error } = await supabase
        .from('activities')
        .update({
          completed: true,
          completion_date: new Date().toISOString()
        })
        .eq('id', activityId);

      if (error) throw error;

      invalidateActivities();

      toast({
        description: "Activity marked as complete",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-activities-tab-handle-complete' });
      toast({
        title: "Error",
        description: "Failed to update activity",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (activityId: string) => {
    try {
      const { error } = await supabase
        .from('activities')
        .delete()
        .eq('id', activityId);

      if (error) throw error;

      invalidateActivities();

      toast({
        description: "Activity deleted successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-activities-tab-handle-delete' });
      toast({
        title: "Error",
        description: "Failed to delete activity",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div className="py-2 text-center text-muted-foreground text-sm">Loading activities...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-semibold text-sm">Activities</h2>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setCreateDialogOpen(true)}
          className="h-7 text-xs"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          New Activity
        </Button>
      </div>

      <div className="space-y-2">
        {activities.map((activity) => (
          <div
            key={activity.id}
            className="group relative p-3 rounded-lg border hover:bg-accent/5 transition-colors bg-background"
          >
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {activity.type === 'api_update' && (
                    <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0 border-blue-300 text-blue-600">
                      <Webhook className="h-3 w-3" />
                      API
                    </Badge>
                  )}
                  <span className="text-sm font-medium">{activity.title}</span>

                  {activity.scheduled && !activity.completed && isPast(new Date(activity.start_date)) && (
                    <Badge variant="destructive" className="text-xs">Overdue</Badge>
                  )}
                  {activity.scheduled && !activity.completed && !isPast(new Date(activity.start_date)) && (
                    <Badge variant="secondary" className="text-xs">Scheduled</Badge>
                  )}
                  {activity.scheduled && activity.completed && <Badge variant="default" className="text-xs">Completed</Badge>}
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {activity.scheduled && !activity.completed && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleComplete(activity.id)}
                      className="h-7 text-xs"
                    >
                      <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                      Complete
                    </Button>
                  )}
                  {isEditableActivity(activity) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditingActivity(activity)}
                      className="h-6 w-6"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(activity.id)}
                    className="h-6 w-6 text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {activity.description && (
                <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{activity.description}</p>
              )}

              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {format(new Date(activity.start_date), 'MM/dd/yyyy h:mm a')}
                </div>
                {activity.end_date && (
                  <div className="flex items-center gap-1">
                    <span>→</span>
                    {format(new Date(activity.end_date), 'MM/dd/yyyy h:mm a')}
                  </div>
                )}
                {activity.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    {activity.location}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {activities.length === 0 && (
          <div className="text-center py-4 text-xs text-muted-foreground">
            No activities yet. Create your first activity above.
          </div>
        )}
      </div>

      <CreateActivityDialog
        open={createDialogOpen || !!editingActivity}
        onOpenChange={(open) => {
          if (!open) {
            setCreateDialogOpen(false);
            setEditingActivity(null);
          }
        }}
        contactId={contactId}
        onSuccess={invalidateActivities}
        activity={editingActivity}
      />
    </div>
  );
}
